<template>
    <transition name="flyout">
        <div
            v-if="show"
            ref="container"
            class="flyout"
            :class="{ 'has-chrome': chrome }"
            :style="positionStyle"
            role="menu"
            :aria-label="ariaLabel"
            @click="$emit('click', $event)"
        >
            <div v-if="chrome && title" class="flyout-title">
                {{ title }}<span
                    v-if="titleBadge"
                    class="flyout-title-badge"
                > {{ titleBadge }}</span>
            </div>
            <slot />
        </div>
    </transition>
</template>

<script>
import { ref, watch } from "@vue/composition-api";
import { useClickOutside } from "@/composables/useClickOutside";

export default {
    name: "SidebarFlyout",
    props: {
        // Visibilidad. El padre controla; aquí solo renderizamos según.
        show: { type: Boolean, required: true },
        // Modo de ancla vertical: top (crece hacia abajo desde y=anchorValue)
        // o bottom (crece hacia arriba desde y=viewport-anchorValue).
        anchorMode: {
            type: String,
            default: "top",
            validator: (v) => ["top", "bottom"].includes(v),
        },
        anchorValue: { type: Number, default: 0 },
        // Card chrome (bg + border + shadow + padding). False cuando el slot
        // trae su propia card (e.g., UserMenu).
        chrome: { type: Boolean, default: true },
        title: { type: String, default: "" },
        // Texto secundario inline al lado del título (e.g., "(beta)").
        titleBadge: { type: String, default: "" },
        ariaLabel: { type: String, default: "" },
        // Selector que se ignora en outside-click. Útil para que el trigger
        // que abre el flyout no dispare auto-close al ser clickeado de nuevo.
        excludeSelector: { type: String, default: "" },
    },
    setup(props, { emit }) {
        const container = ref(null);
        const outside = useClickOutside(container, {
            exclude: props.excludeSelector,
        });

        // Wire automático: cuando show pasa a true, activamos el listener;
        // cuando vuelve a false, lo desactivamos. El padre no tiene que
        // recordar enable/disable.
        watch(
            () => props.show,
            (showing) => {
                if (showing) outside.enable(() => emit("close"));
                else outside.disable();
            }
        );

        return { container };
    },
    computed: {
        positionStyle() {
            return this.anchorMode === "bottom"
                ? { bottom: this.anchorValue + "px" }
                : { top: this.anchorValue + "px" };
        },
    },
};
</script>

<style lang="scss" scoped>
.flyout {
    position: fixed;
    left: var(--sidebar-w-collapsed, 64px);
    margin-left: 8px;
    min-width: 220px;
    max-width: 300px;
    z-index: 75;
}

.flyout.has-chrome {
    background-color: var(--sidebar-paper, #ffffff);
    border: 1px solid var(--sidebar-rule, rgba(16, 24, 40, 0.08));
    border-radius: 8px;
    // Shadow whisper-quiet alineado con el feel ledger del sistema.
    box-shadow: 0 2px 6px rgba(16, 24, 40, 0.05);
    padding: 8px;
}

.flyout-title {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--sidebar-ink-faint, #98a2b3);
    padding: 6px 8px 8px;
    border-bottom: 1px solid var(--sidebar-rule, rgba(16, 24, 40, 0.08));
    margin-bottom: 4px;
}

// Badge override: cancelamos el uppercase/letter-spacing del eyebrow padre
// para que "(beta)" se vea italic-lowercase como diferenciador suave.
.flyout-title-badge {
    text-transform: none;
    font-style: italic;
    font-weight: 400;
    letter-spacing: normal;
}

.flyout-enter-active,
.flyout-leave-active {
    transition: opacity var(--app-transition-fast),
        transform var(--app-transition-fast);
}

.flyout-enter,
.flyout-leave-to {
    opacity: 0;
    transform: translateX(-8px);
}
</style>
